import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { LoginFormData } from '../models/AuthModels';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Card, CardHeaderComponent as CardHeader, CardBodyComponent as CardBody } from './ui/Card';
import { colors, typography, spacing, borderRadius, shadows, gradients } from '../styles/designTokens';

// 로그인 폼 컨테이너 스타일
const LoginContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  min-height: 100dvh;
  background: ${gradients.primary};
  padding: ${spacing[5]};
  position: relative;

  @media (max-width: 480px) {
    padding: ${spacing[4]};
    align-items: flex-start;
    padding-top: max(${spacing[10]}, env(safe-area-inset-top));
  }
  
  /* 배경 패턴 */
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(120, 119, 198, 0.2) 0%, transparent 50%);
    pointer-events: none;
  }
`;

// 로그인 카드 스타일
const LoginCard = styled(Card)`
  width: 100%;
  max-width: 420px;
  position: relative;
  z-index: 1;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: ${shadows['2xl']};

  @media (max-width: 480px) {
    max-width: none;
  }
`;

// 로고/제목 스타일
const LoginTitle = styled.h1`
  text-align: center;
  color: ${colors.semantic.text.primary};
  font-size: ${typography.fontSize['3xl']};
  font-weight: ${typography.fontWeight.bold};
  margin-bottom: ${spacing[2]};
  background: ${gradients.primary};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

// 부제목 스타일
const LoginSubtitle = styled.p`
  text-align: center;
  color: ${colors.semantic.text.secondary};
  font-size: ${typography.fontSize.sm};
  margin-bottom: ${spacing[8]};
  font-weight: ${typography.fontWeight.normal};
`;

// 폼 스타일
const LoginFormContainer = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${spacing[6]};
`;

// 로딩 오버레이
const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${borderRadius.xl};
  backdrop-filter: blur(4px);
  z-index: 10;
  
  .spinner {
    width: 2rem;
    height: 2rem;
    border: 3px solid ${colors.primary[200]};
    border-top-color: ${colors.primary[500]};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
`;

// 전체 에러 메시지 스타일
const GlobalErrorMessage = styled.div`
  background: ${colors.error[50]};
  border: 1px solid ${colors.error[200]};
  color: ${colors.error[600]};
  padding: ${spacing[3]} ${spacing[4]};
  border-radius: ${borderRadius.lg};
  font-size: ${typography.fontSize.sm};
  margin-bottom: ${spacing[5]};
`;

// 로그인 폼 Props 인터페이스
interface LoginFormProps {
  onSubmit: (formData: LoginFormData) => Promise<void>;
  isLoading: boolean;
  error: string | null;
  onClearError: () => void;
}

// 로그인 폼 컴포넌트
const LoginForm: React.FC<LoginFormProps> = ({
  onSubmit,
  isLoading,
  error,
  onClearError
}) => {
  // 폼 데이터 상태
  const [formData, setFormData] = useState<LoginFormData>({
    admin_id: '',
    admin_pw: '',
  });

  // 필드별 에러 상태
  const [fieldErrors, setFieldErrors] = useState<{
    admin_id?: string;
    admin_pw?: string;
  }>({});

  // 에러가 변경될 때 필드 에러 파싱
  useEffect(() => {
    if (error) {
      // 전체 에러 메시지에서 필드별 에러 추출 (간단한 예시)
      const errors: { admin_id?: string; admin_pw?: string } = {};

      if (error.includes('ID')) {
        errors.admin_id = error;
      } else if (error.includes('비밀번호')) {
        errors.admin_pw = error;
      }

      setFieldErrors(errors);
    } else {
      setFieldErrors({});
    }
  }, [error]);

  // 입력값 변경 처리
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // 입력 시 해당 필드 에러 클리어
    if (fieldErrors[name as keyof typeof fieldErrors]) {
      setFieldErrors(prev => ({
        ...prev,
        [name]: undefined,
      }));
      onClearError();
    }
  };

  // 폼 제출 처리
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    console.log('LoginForm.handleSubmit 시작:', formData);

    if (isLoading) {
      console.log('이미 로딩 중이므로 제출 무시');
      return;
    }

    console.log('onSubmit 호출 시작');
    await onSubmit(formData);
    console.log('onSubmit 호출 완료');
  };

  return (
    <LoginContainer>
      <LoginCard variant="elevated" size="lg">
        <CardHeader>
          <LoginTitle>관리자 로그인</LoginTitle>
          <LoginSubtitle>BI 관리 시스템에 로그인하세요</LoginSubtitle>
        </CardHeader>

        <CardBody>
          {/* 전체 에러 메시지 */}
          {error && !fieldErrors.admin_id && !fieldErrors.admin_pw && (
            <GlobalErrorMessage>
              {error}
            </GlobalErrorMessage>
          )}

          {/* 로그인 폼 */}
          <LoginFormContainer onSubmit={handleSubmit}>
            {/* 관리자 ID 입력 */}
            <Input
              id="admin_id"
              name="admin_id"
              type="text"
              label="관리자 ID"
              placeholder="관리자 ID를 입력하세요"
              value={formData.admin_id}
              onChange={handleInputChange}
              state={fieldErrors.admin_id ? 'error' : 'default'}
              errorText={fieldErrors.admin_id}
              disabled={isLoading}
              autoComplete="username"
              fullWidth
            />

            {/* 비밀번호 입력 */}
            <Input
              id="admin_pw"
              name="admin_pw"
              type="password"
              label="비밀번호"
              placeholder="비밀번호를 입력하세요"
              value={formData.admin_pw}
              onChange={handleInputChange}
              state={fieldErrors.admin_pw ? 'error' : 'default'}
              errorText={fieldErrors.admin_pw}
              disabled={isLoading}
              autoComplete="current-password"
              fullWidth
            />

            {/* 로그인 버튼 */}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              state={isLoading ? 'loading' : 'default'}
              disabled={isLoading}
              fullWidth
            >
              {isLoading ? '로그인 중...' : '로그인'}
            </Button>
          </LoginFormContainer>
        </CardBody>

        {/* 로딩 오버레이 */}
        {isLoading && (
          <LoadingOverlay>
            <div className="spinner" />
          </LoadingOverlay>
        )}
      </LoginCard>
    </LoginContainer>
  );
};

export default LoginForm; 